/* global globalThis */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Database, Edit2, Copy, Save, Download, Layers, Play, CheckCircle2, AlertTriangle, XCircle, Info, Gauge, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import Editor, { DiffEditor } from '@monaco-editor/react';
import BlueGeckoIcon from '../../../../assets/chatbot.png';
import axiosClientPayload from '../../../../api-client/AxiosClientPayload';
import { showToast } from '../../../common/Toaster';

const SCRIPTLY_API_BASE_CC = process.env.REACT_APP_API_BASE_URL_CC;

const useTableFieldsData = (scLayer, scTableFields) => {
  return useMemo(() => {
    let title = scLayer === 'Extract' ? 'SOURCE TABLES' : 'TARGET TABLES';
    let tableList = [];
    let tableFields = {};

    if (scTableFields?.results) {
      Object.keys(scTableFields.results).forEach(mappingId => {
        const mappingInfo = scTableFields.results[mappingId];
        if (mappingInfo?.status === 'success') {
          if (scLayer === 'Extract') {
            const sources = mappingInfo.discovery?.source_tables_for_db || [];
            sources.forEach(t => {
              if (!tableList.includes(t)) tableList.push(t);
            });
            const srcFields = mappingInfo.fields?.source || {};
            Object.keys(srcFields).forEach(t => {
              const cols = srcFields[t]?.columns || [];
              tableFields[t] = [...new Set([...(tableFields[t] || []), ...cols])];
            });
          } else {
            const targets = mappingInfo.discovery?.target_tables_normalized || [];
            targets.forEach(t => {
              if (!tableList.includes(t)) tableList.push(t);
            });
            const tgtFields = mappingInfo.fields?.target || {};
            Object.keys(tgtFields).forEach(t => {
              const cols = tgtFields[t] || [];
              tableFields[t] = [...new Set([...(tableFields[t] || []), ...cols])];
            });
          }
        }
      });
    }

    return { title, tableList, tableFields };
  }, [scLayer, scTableFields]);
};

const processIssue = (issue, sectionName, key, currentScriptType, activeScriptTab, critical, warning, info) => {
  const issueObj = {
    message: issue.message,
    severity: issue.severity,
    line: issue.line,
    category: sectionName,
    subCategory: key,
    table_name: activeScriptTab,
    script_type: currentScriptType
  };
  if (issue.severity === 'critical') critical.push(issueObj);
  else if (issue.severity === 'warning') warning.push(issueObj);
  else info.push(issueObj);
};

const useSelectedTableStats = (scReviewSummary, activeScriptTab) => {
  return useMemo(() => {
    if (!scReviewSummary?.sections || !activeScriptTab) return null;

    let critical = [];
    let warning = [];
    let info = [];
    let scores = [];
    let scriptType = "SQL";

    Object.entries(scReviewSummary.sections).forEach(([sectionName, sectionData]) => {
      const tableData = sectionData.tables?.find(t => t.table_name === activeScriptTab);
      if (!tableData) return;

      if (typeof tableData.score === 'number') scores.push(tableData.score);
      if (tableData.script_type) scriptType = tableData.script_type;

      const currentScriptType = tableData.script_type || scriptType;

      Object.entries(tableData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(issue => processIssue(issue, sectionName, key, currentScriptType, activeScriptTab, critical, warning, info));
        }
      });
    });

    if (scores.length === 0 && critical.length === 0 && warning.length === 0 && info.length === 0) {
      return null;
    }

    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 100;
    const totalIssues = critical.length + warning.length + info.length;

    let riskLevel = 'LOW';
    if (critical.length > 0) riskLevel = 'HIGH';
    else if (warning.length > 0) riskLevel = 'MEDIUM';

    return {
      table_name: activeScriptTab,
      script_type: scriptType,
      score: avgScore,
      risk_level: riskLevel,
      critical,
      warning,
      info,
      total_issues: totalIssues
    };
  }, [scReviewSummary, activeScriptTab]);
};

const getScoreCircleClass = (score) => {
  if (score >= 90) return 'score-circle-high';
  if (score >= 70) return 'score-circle-mid';
  return 'score-circle-low';
};

const getRiskBadgeClass = (riskLevel) => {
  if (riskLevel === 'HIGH') return 'badge-risk-high';
  if (riskLevel === 'MEDIUM') return 'badge-risk-medium';
  return 'badge-risk-low';
};

const MainEditor = ({
  extractInstance,
  transformInstance,
  scLayer,
  scTableFields,
  scGeneratedSQL,
  scGeneratedScripts = [],
  scType = "SQL",
  scGenerating,
  handleRunQuery,
  scQueryRunning,
  scMappingDocs = [],
  scSelectedMappings = [],
  scReviewSummary = null,
  scTransformationRules = [],
  activeScriptTab,
  setActiveScriptTab,
  scViewMode,
  setScViewMode,
  onFixWithAI,
  scPendingChange
}) => {
  const [activeTab, setActiveTab] = useState('Results');
  const [isTablePanelOpen, setIsTablePanelOpen] = useState(true);
  const [fixingItems, setFixingItems] = useState({});
  const [bottomPanelHeight, setBottomPanelHeight] = useState(280);
  const [isResizingBottom, setIsResizingBottom] = useState(false);

  const startBottomResizing = useCallback((e) => {
    setIsResizingBottom(true);
    e.preventDefault();
  }, []);

  const stopBottomResizing = useCallback(() => {
    setIsResizingBottom(false);
  }, []);

  const doBottomResizing = useCallback((e) => {
    if (isResizingBottom) {
      const el = document.querySelector('.editor-results-container');
      if (el) {
        const bottom = el.getBoundingClientRect().bottom;
        const newHeight = bottom - e.clientY;
        if (newHeight > 100 && newHeight < 600) {
          setBottomPanelHeight(newHeight);
        }
      }
    }
  }, [isResizingBottom]);

  useEffect(() => {
    if (isResizingBottom) {
      globalThis.addEventListener('mousemove', doBottomResizing);
      globalThis.addEventListener('mouseup', stopBottomResizing);
    } else {
      globalThis.removeEventListener('mousemove', doBottomResizing);
      globalThis.removeEventListener('mouseup', stopBottomResizing);
    }
    return () => {
      globalThis.removeEventListener('mousemove', doBottomResizing);
      globalThis.removeEventListener('mouseup', stopBottomResizing);
    };
  }, [isResizingBottom, doBottomResizing, stopBottomResizing]);

  // Automatically select the first script tab when mode or scripts change
  useEffect(() => {
    const list = scViewMode === 'code' ? scGeneratedScripts : scTransformationRules;
    if (list && list.length > 0) {
      if (!activeScriptTab || !list.some(s => s.name === activeScriptTab)) {
        setActiveScriptTab(list[0].name);
      }
    } else {
      setActiveScriptTab(null);
    }
  }, [scGeneratedScripts, scTransformationRules, scViewMode, activeScriptTab, setActiveScriptTab]);

  const activeScriptContent = useMemo(() => {
    const list = scViewMode === 'code' ? scGeneratedScripts : scTransformationRules;
    if (!list || list.length === 0) return null;
    const found = list.find(s => s.name === activeScriptTab);
    return found ? found.content : list[0].content;
  }, [activeScriptTab, scGeneratedScripts, scTransformationRules, scViewMode]);

  const editorValue = activeScriptContent || scGeneratedSQL || "-- Write SQL here, or click 'Load Script' to load generated SQL\n-- Click a table in the schema sidebar to generate DDL";
  const editorLanguage = scType ? scType.toLowerCase() : "sql";

  const [expandedTables, setExpandedTables] = useState({});

  const toggleTable = (tableName) => {
    setExpandedTables(prev => ({ ...prev, [tableName]: !prev[tableName] }));
  };

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessData, setSaveSuccessData] = useState(null);
  const editorRef = React.useRef(null);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const token = localStorage.getItem("token") || "";
      const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

      const currentScript = editorRef.current ? editorRef.current.getValue() : editorValue;

      if (!currentScript) {
        showToast("Error", "No script available to save.", "error");
        return;
      }

      const payload = {
        token: token,
        script: currentScript,
        save_file_name: activeScriptTab.endsWith('.sql') ? activeScriptTab : "",
        organization_config_id: organizationConfigId,
        mapping_file_id: mappingFileIds,
        operation: scLayer,
        share_name: "codecheetah"
      };

      const response = await axiosClientPayload.post(
        // // "https://codecheetah-api-hce8agaqdfgcewem.westeurope-01.azurewebsites.net/api/save",
        `${SCRIPTLY_API_BASE_CC}/api/save`,
        payload
      );

      if (response?.data?.status === "success") {
        setSaveSuccessData(response.data);
      } else {
        showToast("Success", "Script saved successfully.", "success");
      }
    } catch (err) {
      console.error("Failed to save script:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save script.";
      showToast("Error", errorMsg, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    if (!editorValue) return;
    const blob = new Blob([editorValue], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeScriptTab || 'generated_script'}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const { title, tableList, tableFields } = useTableFieldsData(scLayer, scTableFields);
  const selectedTableStats = useSelectedTableStats(scReviewSummary, activeScriptTab);

  return (
    <div className="scriptly-editor-area">
      <div className="editor-status-bar">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Database size={16} className="text-blue-500" />
          </div>
          {scTransformationRules && scTransformationRules.length > 0 ? (
            <div className="segmented-control p-1 bg-slate-200/50 rounded-lg flex gap-1">

              <button
                type="button"
                onClick={() => setScViewMode('code')}
                className={`segment-btn ${scViewMode === 'code' ? 'active shadow-sm' : ''}`}
              >
                Generated Code
              </button>
              <button
                type="button"
                onClick={() => setScViewMode('rules')}
                className={`segment-btn relative ${scViewMode === 'rules' ? 'active shadow-sm' : ''}`}
              >
                Transformation Rules
                {scTransformationRules.length > 0 && scViewMode !== 'rules' && (
                  <span className="absolute -top-1 -right-0.5 w-2 h-2 bg-blue-500 rounded-full border-2 border-slate-50 animate-pulse" />
                )}
              </button>
            </div>
          ) : (
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest px-4">
              Script Editor
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* <button className="status-bar-btn">
            <Edit2 size={12} />
            <span>Update Transformation Rule</span>
          </button> */}

          <button className="status-bar-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            <span>{isSaving ? 'Saving...' : 'Save Current Script'}</span>
          </button>
          <button className="status-bar-btn" onClick={handleExport}>
            <Download size={12} />
            <span>Export</span>
          </button>
        </div>
      </div>

      <div className="sql-container">
        {/* Left Table List */}
        <div
          className="target-tables-panel transition-all duration-300 flex flex-col"
          style={{ marginTop: '12px', width: isTablePanelOpen ? 200 : 40, flexShrink: 0 }}
        >
          <button
            type="button"
            className={`panel-sub-header flex items-center ${isTablePanelOpen ? 'justify-between' : 'justify-center'} cursor-pointer hover:bg-slate-50 transition-colors`}
            onClick={() => setIsTablePanelOpen(!isTablePanelOpen)}
            title={isTablePanelOpen ? "Collapse Panel" : "Expand Panel"}
          >
            {isTablePanelOpen ? (
              <>
                <span className="truncate pr-2">{title}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <PanelLeftClose size={14} className="text-slate-400 hover:text-blue-500 transition-colors" />
                </div>
              </>
            ) : (
              <PanelLeftOpen size={14} className="text-slate-400 hover:text-blue-500 transition-colors shrink-0" />
            )}
          </button>

          {isTablePanelOpen && (
            <div className="table-list mt-2 h-full overflow-y-auto custom-scrollbar border-t border-slate-200 pt-2">
              {tableList.map(table => (
                <div key={table} className="mb-1">
                  <button
                    type="button"
                    className="table-item flex items-center gap-2 cursor-pointer hover:bg-slate-50 py-1.5 px-4 w-full text-left"
                    onClick={() => toggleTable(table)}
                  >
                    <Layers size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 text-[11px] text-slate-600 font-medium truncate">{table}</span>
                    <span className="text-slate-400 shrink-0 transition-transform duration-200">
                      {expandedTables[table] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                  </button>
                  {expandedTables[table] && tableFields[table] && (
                    <div className="pl-9 pr-2 py-1.5 flex flex-col gap-1.5 bg-slate-50">
                      {tableFields[table].map(col => (
                        <div key={col} className="text-[10px] text-slate-500 hover:text-blue-600 cursor-pointer truncate transition-colors">
                          {col}
                        </div>
                      ))}
                      {tableFields[table].length === 0 && (
                        <div className="text-[9px] text-slate-500 italic">No columns found</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {tableList.length === 0 && (
                <div className="text-[10px] text-slate-500 px-4 py-2 italic text-center">
                  No {title.toLowerCase()} available.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center Editor */}
        <div className="flex-1 flex flex-col min-w-0" style={{ borderLeft: '1px solid #e2e8f0' }}>
          {/* Category Toggle & Tab Bar */}
          <div className="flex flex-col border-b border-slate-200">
            <div className="flex items-center px-4 h-10 justify-between">
              <div className="flex flex-1 overflow-x-auto custom-scrollbar items-center gap-1 pt-1">

                {(scViewMode === 'code' ? scGeneratedScripts : scTransformationRules).map((script) => (
                  <button
                    type="button"
                    key={script.name}
                    onClick={() => setActiveScriptTab(script.name)}
                    className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest cursor-pointer whitespace-nowrap border-b-2 transition-colors ${activeScriptTab === script.name
                      ? "border-blue-500 text-blue-600 bg-blue-50"
                      : "border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                      }`}
                  >
                    {script.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden relative min-w-0">
            <div className="flex-1 overflow-hidden min-h-0 shrink relative">
              {/* Diff Editor - Hidden when no pending change */}
              <div
                className="absolute inset-0 z-10"
                style={{ display: (scPendingChange && scPendingChange.tableName === activeScriptTab && scPendingChange.viewMode === scViewMode) ? 'block' : 'none' }}
              >
                <DiffEditor
                  height="100%"
                  theme="vs-light"
                  language={editorLanguage}
                  original={scPendingChange?.oldCode || ""}
                  modified={scPendingChange?.newCode || ""}
                  options={{
                    automaticLayout: true,
                    minimap: { enabled: false },
                    fontSize: 12,
                    scrollBeyondLastLine: false,
                    padding: { top: 10 },
                    renderSideBySide: true,
                    readOnly: true,
                    wordWrap: 'on',
                    diffWordWrap: 'on'
                  }}
                />
              </div>

              {/* Standard Editor - Hidden when reviewing diff */}
              <div
                className="absolute inset-0"
                style={{ display: (scPendingChange && scPendingChange.tableName === activeScriptTab && scPendingChange.viewMode === scViewMode) ? 'none' : 'block' }}
              >
                <Editor
                  height="100%"
                  theme="vs-light"
                  language={editorLanguage}
                  value={editorValue}
                  onMount={handleEditorDidMount}
                  options={{
                    automaticLayout: true,
                    minimap: { enabled: false },
                    fontSize: 12,
                    scrollBeyondLastLine: false,
                    padding: { top: 10 },
                    fontFamily: "'Fira Code', 'Monaco', 'Courier New', monospace",
                    lineNumbers: 'on',
                    glyphMargin: true,
                    folding: true,
                    lineDecorationsWidth: 10,
                    readOnly: scGenerating,
                    wordWrap: 'on'
                  }}
                />
              </div>
            </div>

            <button
              type="button"
              className="results-resizer"
              aria-label="Resize results panel"
              onMouseDown={startBottomResizing}
            />

            {/* Bottom Tabs/Results */}
            <div
              className="border-t border-slate-200 bg-[#ffffff] flex flex-col editor-results-container"
              style={{ height: bottomPanelHeight }}
            >
              <div className="flex items-center px-4 border-b border-slate-100 h-10 gap-6">
                {['Compiled Code', 'Code Quality', 'Lineage', 'Commands'].map(tab => (
                  <button
                    type="button"
                    key={tab}
                    className={`editor-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
                <div className="ml-auto" style={{ marginLeft: 'auto' }}>
                  {/* <button
                    onClick={handleRunQuery}
                    disabled={scQueryRunning}
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 text-[10px] font-bold px-4 py-1.5 rounded flex items-center gap-1.5 transition-all shadow-lg active:scale-95"
                  >
                    <Play size={10} fill="currentColor" />
                    Validate
                  </button> */}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50/50">
                {activeTab === 'Results' && (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3 h-full">
                    <Database size={40} className="opacity-10" />
                    <span className="text-[11px] font-medium tracking-wide">Run a query to see results</span>
                  </div>
                )}

                {activeTab === 'Code Quality' && (
                  <div className="quality-dashboard-container">
                    {selectedTableStats ? (
                      <>
                        {/* 1. SCRIPT SUMMARY DASHBOARD */}
                        <div className="dashboard-summary-grid">
                          <div className="stat-card">
                            <div className="stat-card-label">Script Quality</div>
                            <div className="stat-card-value text-blue-600">
                              {selectedTableStats.score?.toFixed(0)}%
                              <div className={`ml-auto score-circle ${getScoreCircleClass(selectedTableStats.score)}`}>
                                <Gauge size={14} />
                              </div>
                            </div>
                            <div className="stat-card-subtext">Table: <span className="font-bold">{selectedTableStats.table_name}</span></div>
                          </div>

                          <div className="stat-card">
                            <div className="stat-card-label">Risk Level</div>
                            <div className="stat-card-value">
                              <span className={`badge-pill ${getRiskBadgeClass(selectedTableStats.risk_level)}`}>
                                {selectedTableStats.risk_level}
                              </span>
                            </div>
                            <div className="stat-card-subtext flex items-center gap-1">
                              <ShieldAlert size={10} className={selectedTableStats.risk_level === 'HIGH' ? 'text-red-500' : 'text-amber-500'} />
                              {selectedTableStats.total_issues === 0 ? 'Ready for Deployment' : `${selectedTableStats.total_issues} Issues Found`}
                            </div>
                          </div>

                          <div className="stat-card">
                            <div className="stat-card-label">Severity Breakdown</div>
                            <div className="flex items-center gap-3 mt-1">
                              <div className="flex flex-col">
                                <span className="text-red-500 text-[14px] font-bold">{selectedTableStats.critical.length}</span>
                                <span className="text-[8px] uppercase text-slate-400 font-bold">Critical</span>
                              </div>
                              <div className="w-[1px] h-6 bg-slate-100" />
                              <div className="flex flex-col">
                                <span className="text-amber-500 text-[14px] font-bold">{selectedTableStats.warning.length}</span>
                                <span className="text-[8px] uppercase text-slate-400 font-bold">Warning</span>
                              </div>
                              <div className="w-[1px] h-6 bg-slate-100" />
                              <div className="flex flex-col">
                                <span className="text-blue-500 text-[14px] font-bold">{selectedTableStats.info.length}</span>
                                <span className="text-[8px] uppercase text-slate-400 font-bold">Info</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 2. DETAILED REVIEW SECTIONS */}
                        <div className="flex flex-col gap-6 mt-4 pb-20">
                          {/* SECTION: CRITICAL */}
                          {selectedTableStats.critical.length > 0 && (
                            <div>
                              <div className="table-review-section-title">
                                <XCircle size={14} className="text-red-500" />
                                Critical Issues - Needs Urgent Fixes ({selectedTableStats.critical.length})
                              </div>
                              <div className="grid grid-cols-1 gap-4">
                                <div className="bg-white border-l-4 border-red-500 rounded-lg shadow-sm border border-slate-200 p-3 hover:border-red-200 transition-colors">
                                  <div className="flex flex-col gap-1.5">
                                    {selectedTableStats.critical.map((issue, cIdx) => {
                                      const fixKey = `critical-${issue.table_name}-${cIdx}`;
                                      const isFixing = fixingItems[fixKey];
                                      return (
                                        <div key={fixKey} className="fix-ai-row">
                                          <div className="flex items-start gap-2 text-[12px] text-slate-600 flex-1 min-w-0">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                                            <span className="flex-1">
                                              <strong className="text-slate-800 capitalize mr-1">[{issue.category}]</strong>
                                              {issue.message}
                                              {issue.line && <span className="text-slate-400 ml-1">(Line/Section: {issue.line})</span>}
                                            </span>
                                          </div>
                                          {onFixWithAI && (
                                            <button
                                              type="button"
                                              className="fix-ai-btn fix-ai-btn-red"
                                              disabled={isFixing}
                                              onClick={async () => {
                                                setFixingItems(prev => ({ ...prev, [fixKey]: true }));
                                                try { await onFixWithAI(issue.table_name, issue.message, issue.script_type); }
                                                finally { setFixingItems(prev => ({ ...prev, [fixKey]: false })); }
                                              }}
                                            >
                                              {isFixing ? (
                                                <Loader2 size={10} className="animate-spin" />
                                              ) : (
                                                <img src={BlueGeckoIcon} alt="AI" className="w-3 h-3 object-contain" />
                                              )}
                                              <span>{isFixing ? 'Fixing...' : 'Fix with AI'}</span>
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* SECTION: WARNING */}
                          {selectedTableStats.warning.length > 0 && (
                            <div>
                              <div className="table-review-section-title">
                                <AlertTriangle size={14} className="text-amber-500" />
                                Quality Warnings - Review Recommended ({selectedTableStats.warning.length})
                              </div>
                              <div className="grid grid-cols-1 gap-4">
                                <div className="bg-white border-l-4 border-amber-500 rounded-lg shadow-sm border border-slate-200 p-3 hover:border-amber-200 transition-colors">
                                  <div className="flex flex-col gap-1.5">
                                    {selectedTableStats.warning.map((issue, cIdx) => {
                                      const fixKey = `warning-${issue.table_name}-${cIdx}`;
                                      const isFixing = fixingItems[fixKey];
                                      return (
                                        <div key={fixKey} className="fix-ai-row">
                                          <div className="flex items-start gap-2 text-[12px] text-slate-600 flex-1 min-w-0">
                                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                                            <span className="flex-1">
                                              <strong className="text-slate-800 capitalize mr-1">[{issue.category}]</strong>
                                              {issue.message}
                                              {issue.line && <span className="text-slate-400 ml-1">(Line/Section: {issue.line})</span>}
                                            </span>
                                          </div>
                                          {onFixWithAI && (
                                            <button
                                              type="button"
                                              className="fix-ai-btn fix-ai-btn-amber"
                                              disabled={isFixing}
                                              onClick={async () => {
                                                setFixingItems(prev => ({ ...prev, [fixKey]: true }));
                                                try { await onFixWithAI(issue.table_name, issue.message, issue.script_type); }
                                                finally { setFixingItems(prev => ({ ...prev, [fixKey]: false })); }
                                              }}
                                            >
                                              {isFixing ? (
                                                <Loader2 size={10} className="animate-spin" />
                                              ) : (
                                                <img src={BlueGeckoIcon} alt="AI" className="w-3 h-3 object-contain" />
                                              )}
                                              <span>{isFixing ? 'Fixing...' : 'Fix with AI'}</span>
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* SECTION: INFO */}
                          {selectedTableStats.info.length > 0 && (
                            <div>
                              <div className="table-review-section-title">
                                <Info size={14} className="text-blue-500" />
                                Suggestions & Info ({selectedTableStats.info.length})
                              </div>
                              <div className="grid grid-cols-1 gap-4">
                                <div className="bg-white border-l-4 border-blue-500 rounded-lg shadow-sm border border-slate-200 p-3 hover:border-blue-200 transition-colors">
                                  <div className="flex flex-col gap-1.5">
                                    {selectedTableStats.info.map((issue, cIdx) => {
                                      const fixKey = `info-${issue.table_name}-${cIdx}`;
                                      const isFixing = fixingItems[fixKey];
                                      return (
                                        <div key={fixKey} className="fix-ai-row">
                                          <div className="flex items-start gap-2 text-[12px] text-slate-600 flex-1 min-w-0">
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                                            <span className="flex-1">
                                              <strong className="text-slate-800 capitalize mr-1">[{issue.category}]</strong>
                                              {issue.message}
                                              {issue.line && <span className="text-slate-400 ml-1">(Line/Section: {issue.line})</span>}
                                            </span>
                                          </div>
                                          {onFixWithAI && (
                                            <button
                                              type="button"
                                              className="fix-ai-btn flex items-center gap-1.5 px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider transition-all bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
                                              disabled={isFixing}
                                              onClick={async () => {
                                                setFixingItems(prev => ({ ...prev, [fixKey]: true }));
                                                try { await onFixWithAI(issue.table_name, issue.message, issue.script_type); }
                                                finally { setFixingItems(prev => ({ ...prev, [fixKey]: false })); }
                                              }}
                                            >
                                              {isFixing ? (
                                                <Loader2 size={10} className="animate-spin" />
                                              ) : (
                                                <img src={BlueGeckoIcon} alt="AI" className="w-3 h-3 object-contain" />
                                              )}
                                              <span>{isFixing ? 'Fixing...' : 'Fix with AI'}</span>
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* SECTION: GOOD */}
                          {selectedTableStats.total_issues === 0 && (
                            <div>
                              <div className="table-review-section-title">
                                <CheckCircle2 size={14} className="text-emerald-500" />
                                Clean Script - Ready for Deployment
                              </div>
                              <div className="grid grid-cols-1 gap-4">
                                <div className="bg-white border-l-4 border-emerald-500 rounded-lg shadow-sm border border-slate-200 p-3 hover:bg-emerald-50/30 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <ShieldCheck size={14} className="text-emerald-500" />
                                    <span className="text-[12px] font-bold text-slate-800">{selectedTableStats.table_name}</span>
                                    <span className="ml-auto px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[8px] font-bold uppercase tracking-wider">Passed all checks</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="h-[300px] flex flex-col items-center justify-center text-slate-400 gap-3 bg-white rounded-xl border-2 border-dashed border-slate-200">
                        <Gauge size={48} className="opacity-10" />
                        <div className="text-center">
                          <p className="text-[13px] font-bold text-slate-600">No Review Data Available</p>
                          <p className="text-[11px] font-medium tracking-wide">Select a script or generate one to see code quality analysis</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab !== 'Results' && activeTab !== 'Code Quality' && (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3 h-full">
                    <Info size={40} className="opacity-10" />
                    <span className="text-[11px] font-medium tracking-wide">Content for {activeTab} is currently Under Progress.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Success Modal */}
      {saveSuccessData && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 w-full max-w-[320px] overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-5 text-center flex flex-col items-center">
              <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 size={20} className="text-emerald-500" />
              </div>
              <h3 className="text-[15px] font-bold text-slate-800 mb-1.5">Script saved successfully!</h3>
              <p className="text-[11px] text-slate-500 mb-5 leading-relaxed px-2">
                Your transformation script has been generated and saved successfully.
              </p>

              {saveSuccessData.saved_files && saveSuccessData.saved_files.length > 0 && (
                <div className="w-full bg-slate-50/80 border border-slate-100 rounded-lg p-2.5 mb-5 flex items-center justify-between group hover:border-slate-200 transition-colors">
                  <div className="flex flex-col min-w-0 pr-3 text-left">
                    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Saved File</div>
                    <div className="text-[11px] font-medium text-slate-700 font-mono truncate flex items-center gap-1.5">
                      <span className="truncate">{saveSuccessData.saved_files[0].file_name || 'script.sql'}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    title="Copy filename"
                    onClick={() => {
                      navigator.clipboard.writeText(saveSuccessData.saved_files[0].file_name || 'script.sql');

                    }}
                    className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors shrink-0"
                  >
                    <Copy size={12} />
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => setSaveSuccessData(null)}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-bold rounded-lg transition-colors shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MainEditor.propTypes = {
  extractInstance: PropTypes.string,
  transformInstance: PropTypes.string,
  scLayer: PropTypes.string,
  scTableFields: PropTypes.object,
  scGeneratedSQL: PropTypes.string,
  scGeneratedScripts: PropTypes.array,
  scType: PropTypes.string,
  scGenerating: PropTypes.bool,
  handleRunQuery: PropTypes.func,
  scQueryRunning: PropTypes.bool,
  scMappingDocs: PropTypes.array,
  scSelectedMappings: PropTypes.array,
  scReviewSummary: PropTypes.object,
  scTransformationRules: PropTypes.array,
  activeScriptTab: PropTypes.string,
  setActiveScriptTab: PropTypes.func,
  scViewMode: PropTypes.string,
  setScViewMode: PropTypes.func,
  onFixWithAI: PropTypes.func,
  scPendingChange: PropTypes.object
};

export default MainEditor;